// peptideStock.js — frontend stock helpers.
//
// Mirrors lucasai-backend/backend/lib/peptideStock.js for read-side projection.
// Backend computes status from raw quantities; the frontend trusts that result
// and never tries to recompute on-hand math (we don't have raw quantities for
// non-manufacturer roles). We DO have small helper functions that classify a
// projected `variant.stock` blob into render-time concepts: "can I add this
// to my cart?", "should I show 'Only N left'?", "is the 'Notify me' CTA the
// right replacement for 'Add to cart'?".
//
// Shape contract — what the backend returns per variant.stock:
//
//   manufacturer:
//     { onHandQty, reservedQty, availableQty, lowStockThreshold,
//       expectedRestockDate, status, discontinued, updatedAt, updatedBy }
//
//   provider / patient (role-projected):
//     { availableQty: <number; null only for out_of_stock / discontinued>,
//       status: "in_stock" | "low_stock" | "back_ordered" | "out_of_stock" |
//               "discontinued",
//       expectedRestockDate: ISO-8601 string | null }
//
// Treat missing `stock` field as "unknown" (legacy catalog row pre-stock-rollout)
// rather than guessing OOS — pre-rollout catalogs should still let users add to
// cart. Backend will block at order-create time once Slice C ships reservations.

export const STOCK_STATUS = Object.freeze({
  IN_STOCK: "in_stock",
  LOW_STOCK: "low_stock",
  BACK_ORDERED: "back_ordered",
  OUT_OF_STOCK: "out_of_stock",
  DISCONTINUED: "discontinued",
});

const KNOWN_STATUSES = new Set(Object.values(STOCK_STATUS));

// `projectVariantStock(row)` — manufacturer-side projection helper.
//
// The provider/patient `Get Catalog` RPCs ship variants with `variant.stock`
// already populated by the backend (`lib/peptideStock.js`). The manufacturer
// `getPeptideData()` returns the raw `org.peptideCatalog`, and inventory lives
// in a sibling doc fetched via `getAllPeptideStock`. Catalog callers that want
// the manufacturer view to render the same OOS / low-stock badges as providers
// need to merge those rows back onto variants — that's what this helper is for.
//
// Logic mirrors the backend's status derivation (and the demoInterceptor /
// BulkInventoryGrid rowStatus): discontinued wins, then available <= 0 splits
// into back_ordered (when a restock date exists) vs out_of_stock, then a
// low-stock threshold check, otherwise in_stock. `availableQty` is ALWAYS the
// computed `onHand - reserved` (>= 0) — matching the backend manufacturer
// projection (lib/peptideStock.js `projectStockForRole(row, "manufacturer")`,
// which ships availableQty unconditionally). It MUST be a number for every
// row so the cart's partial-stock detection (isPartialStock → split-ship) can
// fire. A prior `low_stock ? available : null` gating here silently broke
// split-ship for IN-STOCK manufacturer lines (requested qty > available, but
// available above the low-stock threshold): availableQty came back null, the
// cart never stamped "split_ship", and the whole line back-ordered. The
// "Only N left" badge stays low-stock-only because `lowStockCount` gates on
// STATUS, not on availableQty being null.
export function projectVariantStock(row) {
  if (!row) return null;
  const onHand = parseInt(row.onHandQty, 10) || 0;
  const reserved = parseInt(row.reservedQty, 10) || 0;
  const available = Math.max(0, onHand - reserved);
  const threshold = parseInt(row.lowStockThreshold, 10) || 0;
  const expectedRestockDate = row.expectedRestockDate || null;
  let status;
  if (row.discontinued) {
    status = STOCK_STATUS.DISCONTINUED;
  } else if (available <= 0) {
    status = expectedRestockDate ? STOCK_STATUS.BACK_ORDERED : STOCK_STATUS.OUT_OF_STOCK;
  } else if (available <= threshold) {
    status = STOCK_STATUS.LOW_STOCK;
  } else {
    status = STOCK_STATUS.IN_STOCK;
  }
  return {
    status,
    availableQty: available,
    expectedRestockDate,
  };
}

// `mergeStockIntoCatalog(catalog, stockRows)` — clones a manufacturer catalog
// and writes a projected `variant.stock` onto each variant, keyed by SKU
// (variant.id). Variants without a matching stock row are left untouched so
// the UI's "stock unknown" branch still kicks in for legacy / unseeded rows.
export function mergeStockIntoCatalog(catalog, stockRows) {
  if (!Array.isArray(catalog) || catalog.length === 0) return catalog || [];
  const bySku = {};
  for (const row of stockRows || []) {
    if (row && row.sku) bySku[row.sku] = row;
  }
  return catalog.map((product) => ({
    ...product,
    variants: (product.variants || []).map((variant) => {
      const row = bySku[variant?.id];
      if (!row) return variant;
      return { ...variant, stock: projectVariantStock(row) };
    }),
  }));
}

// `variantStockOf(variant)` is the single accessor the UI should call. It
// returns either a stock object or null. Centralizing this means a future
// schema migration (e.g. moving stock from `variant.stock` to a top-level
// `variant.fulfillment.stock`) only needs to change this one function.
export function variantStockOf(variant) {
  if (!variant) return null;
  const s = variant.stock;
  if (s == null) return null;
  return s;
}

export function isStockUnknown(stock) {
  return stock == null || stock.status == null || !KNOWN_STATUSES.has(stock.status);
}

export function isOutOfStock(stock) {
  if (isStockUnknown(stock)) return false;
  return stock.status === STOCK_STATUS.OUT_OF_STOCK || stock.status === STOCK_STATUS.BACK_ORDERED;
}

export function isDiscontinued(stock) {
  return !isStockUnknown(stock) && stock.status === STOCK_STATUS.DISCONTINUED;
}

// `isLowStock` is non-blocking — patients/providers can still buy, but the
// catalog badge surfaces the count so they know to act fast.
export function isLowStock(stock) {
  return !isStockUnknown(stock) && stock.status === STOCK_STATUS.LOW_STOCK;
}

// `canPurchase` is the single FE gate for "should I render Add-to-Cart?".
// Only DISCONTINUED variants are blocked — those are permanently gone and
// no amount of waiting will restock them. OOS / back_ordered variants ARE
// addable: the backend accepts them, marks the order line back_ordered, and
// the allocator promotes the reservation when stock arrives. UI surfaces the
// back-order nature via the badge + a stepper caption, but the CTA is the
// same QuantityStepper as a normal in-stock variant. Source of truth for
// availability still happens at order-create on the backend.
export function canPurchase(stock) {
  if (isStockUnknown(stock)) return true; // legacy catalog row, optimistic
  if (isDiscontinued(stock)) return false;
  return true;
}

// `isBackOrderable` says "addable, but won't ship today — the user is
// committing to a back-order." OOS or back_ordered status both qualify;
// discontinued doesn't (canPurchase already blocks that). Use this to render
// the back-order caption + cart-level warning. Distinct from `canPurchase`
// because callers want to know *which* of "ship now" vs "back-order" applies,
// not just "can I add this."
export function isBackOrderable(stock) {
  if (isStockUnknown(stock)) return false;
  if (isDiscontinued(stock)) return false;
  return stock.status === STOCK_STATUS.OUT_OF_STOCK || stock.status === STOCK_STATUS.BACK_ORDERED;
}

// `canSubscribe` is "should we show 'Notify me' for this variant?" — yes when
// OOS or back-ordered (same set as isBackOrderable, but kept as a distinct
// concept so future copy can differentiate the passive "ping me when in
// stock" CTA from the active back-order commitment). Discontinued products
// won't restock so we don't offer subscription for those.
export function canSubscribe(stock) {
  return isBackOrderable(stock);
}

// `hasInsufficientStockForQty` catches the case where stock.status reads
// "in_stock" but the requested quantity exceeds availability — the backend
// allocator will back-order that line, so the cart should flag it up-front
// instead of surprising the user post-checkout. Falls back to false when
// availableQty isn't exposed (legacy in_stock rows), so it's safe to OR with
// isBackOrderable without false positives.
export function hasInsufficientStockForQty(stock, qty) {
  if (qty == null || qty <= 0) return false;
  if (isStockUnknown(stock)) return false;
  if (isDiscontinued(stock)) return false;
  // OOS / back_ordered already qualifies via isBackOrderable; this helper
  // covers the under-the-radar case (in_stock or low_stock with availableQty
  // < requested qty).
  if (isOutOfStock(stock)) return true;
  const available = parseInt(stock.availableQty, 10);
  if (!Number.isFinite(available)) return false;
  const requested = parseInt(qty, 10);
  if (!Number.isFinite(requested)) return false;
  return available < requested;
}

// `lowStockCount` returns the integer to show in "Only N left". Returns null
// when the count shouldn't be shown (in_stock, unknown, or OOS — count is 0).
// `availableQty` is now exposed for in-stock rows too, so the gate here is
// `isLowStock` (STATUS) — NOT "availableQty is non-null" — to keep the
// "Only N left" badge low-stock-only. The defensive parse below still guards
// a mis-shaped response from rendering "Only null left".
export function lowStockCount(stock) {
  if (!isLowStock(stock)) return null;
  const n = parseInt(stock.availableQty, 10);
  if (isNaN(n) || n <= 0) return null;
  return n;
}

// `expectedRestockDate` is informational; renderers should format with the
// active locale. Returns null when missing or not parseable.
export function expectedRestockDate(stock) {
  if (isStockUnknown(stock)) return null;
  const d = stock.expectedRestockDate;
  if (!d) return null;
  // Tolerate both ISO strings and epoch ms.
  if (typeof d === "number") return new Date(d);
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
}

// `cartGuardSummary(items)` walks cart items and flags any variants the user
// cannot currently order. Only DISCONTINUED items are blocked now — OOS /
// back_ordered items are addable and ship when stock arrives. Returns:
//   { blocked: boolean, blockedItems: Array<{variantId, status}>, count: number }
// The cart UI renders inline warnings per blocked item and disables checkout
// when blocked === true.
export function cartGuardSummary(items) {
  if (!items || items.length === 0) {
    return { blocked: false, blockedItems: [], count: 0 };
  }
  const blockedItems = [];
  for (const item of items) {
    const s = variantStockOf(item.variant);
    if (!canPurchase(s)) {
      blockedItems.push({
        variantId: item.variant?.id,
        productName: item.product?.name,
        status: s?.status || STOCK_STATUS.DISCONTINUED,
      });
    }
  }
  return {
    blocked: blockedItems.length > 0,
    blockedItems,
    count: blockedItems.length,
  };
}

// `backOrderSummary(items)` counts cart lines that will be back-ordered. Two
// reasons a line back-orders:
//   1. `isBackOrderable(stock)` — variant status is OOS / back_ordered
//   2. `hasInsufficientStockForQty(stock, qty)` — variant is "in_stock" but the
//      requested qty exceeds availableQty. The backend allocator will
//      back-order this line at create time, so the cart must surface it.
// Discontinued items are excluded — cartGuardSummary blocks those separately.
//
// Used for the soft "N items will be back-ordered" notice in the cart's
// checkout summary box. NOT a checkout blocker.
export function backOrderSummary(items) {
  if (!items || items.length === 0) {
    return { count: 0, items: [] };
  }
  const backOrderItems = [];
  for (const item of items) {
    const s = variantStockOf(item.variant);
    if (isBackOrderable(s) || hasInsufficientStockForQty(s, item.quantity)) {
      backOrderItems.push({
        variantId: item.variant?.id,
        productName: item.product?.name,
        expectedRestockDate: s?.expectedRestockDate || null,
      });
    }
  }
  return { count: backOrderItems.length, items: backOrderItems };
}
